import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import settingsService from '../../../../services/settings.service';
import { confirmDelete, showError, showSuccess } from '../../../../utils/ecxSwal';
import { ClinicInfoPanel } from './info/clinicInfoParts';
import ClinicSettingsSectionHeader from './subparts/ClinicSettingsSectionHeader';
import ClinicSettingsEmpty from './subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from './subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from './subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from './subparts/ClinicSettingsListSkeleton';

const ClBrands = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const response = await settingsService.getCLBrands(clinicId);
            setBrands(response.data);
        } catch (error) {
            console.error('Error fetching brands:', error);
            showError({
                title: 'Load Failed',
                text: 'Unable to load contact lens brands. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const addBrand = async (data) => {
        setIsSaving(true);
        try {
            const brandData = {
                brandName: data.brandName,
                isActive: data.status,
                clinicId,
            };
            const response = await settingsService.addUpdateCLBrands(clinicId, brandData);
            setBrands((prev) => [...prev, response.data]);
            setAddingNew(false);
            reset();
            await showSuccess({
                title: 'Brand Added',
                text: 'The contact lens brand was added successfully.',
            });
        } catch (error) {
            console.error('Error adding brand:', error);
            showError({
                title: 'Add Failed',
                text: 'Unable to add the brand. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const updateBrand = async (brandId, data) => {
        setIsSaving(true);
        try {
            const updatedBrandData = {
                id: brandId,
                brandName: data.brandName,
                isActive: data.status,
                clinicId,
            };
            const response = await settingsService.addUpdateCLBrands(clinicId, updatedBrandData);
            setBrands((prev) =>
                prev.map((brand) =>
                    brand.id === brandId
                        ? { ...brand, ...response.data, brandName: data.brandName, isActive: data.status }
                        : brand
                )
            );
            setEditingId(null);
            reset();
            await showSuccess({
                title: 'Brand Updated',
                text: 'The contact lens brand was updated successfully.',
            });
        } catch (error) {
            console.error('Error updating brand:', error);
            showError({
                title: 'Update Failed',
                text: 'Unable to update the brand. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const deleteBrand = async (brand) => {
        const result = await confirmDelete({
            title: 'Delete Brand?',
            itemName: brand.brandName,
            description: 'This brand will be removed from your clinic settings.',
            onConfirm: () => settingsService.deleteCLBrands(clinicId, brand.id),
        });

        if (result.isConfirmed) {
            setBrands((prev) => prev.filter((item) => item.id !== brand.id));
        }
    };

    const handleEdit = (brand) => {
        setEditingId(brand.id);
        setAddingNew(false);
        setValue('brandName', brand.brandName);
        setValue('status', brand.isActive);
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({ brandName: '', status: true });
    };

    useEffect(() => {
        fetchBrands();
    }, [clinicId]);

    const renderBrandForm = (onSubmit, submitLabel) => (
        <form onSubmit={handleSubmit(onSubmit)} className="clinic-settings-record-form">
            <div className="clinic-settings-record-form__row">
                <div>
                    <label className="form-label small fw-normal text-muted">Brand Name</label>
                    <input
                        type="text"
                        className={`form-control form-control-sm ${errors.brandName ? 'is-invalid' : ''}`}
                        placeholder="Enter brand name..."
                        {...register('brandName', {
                            required: 'Brand name is required',
                            minLength: { value: 2, message: 'Brand name must be at least 2 characters' },
                        })}
                    />
                    {errors.brandName && <div className="invalid-feedback">{errors.brandName.message}</div>}
                </div>
                <div className="d-flex align-items-end">
                    <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" {...register('status')} defaultChecked />
                        <label className="form-check-label small">Active</label>
                    </div>
                </div>
            </div>
            <ClinicSettingsFormFooter isSaving={isSaving} submitLabel={submitLabel} />
        </form>
    );

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Contact Lens Brands"
                icon="bi-eye"
                subtitle="Manage your contact lens brand inventory"
                actions={
                    !loading ? (
                        addingNew ? (
                            <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
                        ) : (
                            <button className="btn btn-sm btn-light-primary rounded-pill" onClick={handleAddNew} type="button">
                                <i className="bi bi-plus-circle me-1"></i>
                                Add New Brand
                            </button>
                        )
                    ) : null
                }
            />

            {loading ? (
                <ClinicInfoPanel title="Brands" icon="bi-tags">
                    <ClinicSettingsListSkeleton rows={4} />
                </ClinicInfoPanel>
            ) : (
                <ClinicInfoPanel
                    title="Brands"
                    icon="bi-tags"
                    actions={editingId ? <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} /> : null}
                >
                    {addingNew && renderBrandForm(addBrand, 'Add Brand')}

                    {brands.length === 0 && !addingNew ? (
                        <ClinicSettingsEmpty
                            icon="bi-inbox"
                            title="No brands found"
                            description="Get started by adding your first contact lens brand."
                            actionLabel="Add First Brand"
                            onAction={handleAddNew}
                        />
                    ) : (
                        <div className="clinic-settings-record-list">
                            {brands.map((brand) => (
                                <div
                                    key={brand.id}
                                    className={`clinic-settings-record-item ${editingId === brand.id ? 'clinic-settings-record-item--editing' : ''}`}
                                >
                                    {editingId === brand.id ? (
                                        <div className="flex-grow-1">
                                            {renderBrandForm((data) => updateBrand(brand.id, data), 'Save')}
                                        </div>
                                    ) : (
                                        <>
                                            <span className="clinic-settings-record-item__avatar">
                                                <i className="bi bi-tag"></i>
                                            </span>
                                            <div className="clinic-settings-record-item__main">
                                                <h6 className="clinic-settings-record-item__title">{brand.brandName}</h6>
                                                <p className="clinic-settings-record-item__meta">
                                                    Created {brand.createdAt || '—'} · Modified {brand.modifiedAt || '—'}
                                                </p>
                                            </div>
                                            <span className={`clinic-settings-status-pill ${brand.isActive ? 'is-open' : 'is-closed'}`}>
                                                {brand.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <div className="clinic-settings-record-item__actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary rounded-circle"
                                                    onClick={() => handleEdit(brand)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger rounded-circle"
                                                    onClick={() => deleteBrand(brand)}
                                                    title="Delete"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </ClinicInfoPanel>
            )}
        </>
    );
};

export default ClBrands;
